import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  TooltipContent,
  TooltipCore,
  TooltipTrigger,
} from "@altimateai/lego";
import { useState } from "react";
import { LoadingButton, Stack } from '@altimateai/lego'
import { OwnershipConditionKeys } from "./types";
import { toast } from "@altimateai/lego";
import { AddIcon } from "@/assets/icons";
import { IconTrash } from "@tabler/icons-react";
import { useActiveUserOptions } from "@/helpers/hooks";
import OwnershipConditionValueField from "./OwnershipConditionValueField";
import OwnershipFormFields from "./OwnershipFormFields";
import OwnershipTagFields from "./OwnershipTagFields";
import OwnershipCostBanner from "./OwnershipCostBanner";
import { buildOwnershipRequests } from "./ownershipRequestBuilder";
import { useOwnershipCostPreview } from "./useOwnershipCostPreview";
import ViewConflictsModal from "./ViewConflictsModal";
import ConflictingTeamsModal from "./ConflictingTeamsModal";
import { useAppState } from "@/modules/app/useAppContext";
import { useOwnershipForm } from "./useOwnershipForm";

interface Props {
  groupId: string;
}

const AddNewOwnership = ({ groupId }: Props) => {
  const { enableCostAttributionInTeams } = useAppState();
  const [open, setOpen] = useState(false);
  const [conflictsModalOpen, setConflictsModalOpen] = useState(false);
  const [conflictingTeamsModalOpen, setConflictingTeamsModalOpen] = useState(false);

  const { form, fields, remove, addOwnership, onSubmit, validateForm, isLoading } =
    useOwnershipForm(groupId, () => setOpen(false));

  const watchedOwnerships = form.watch("ownerships");
  const {
    costPreview,
    isCostPreviewLoading,
    costPreviewError,
    hasPreviewableOwnerships,
    refreshCostPreview,
    ownershipRequests,
    dateRange,
  } = useOwnershipCostPreview(enableCostAttributionInTeams ? watchedOwnerships : []);

  const userOptions = useActiveUserOptions();

  const hasValidOwnerships = (requests: ReturnType<typeof buildOwnershipRequests>) =>
    requests.some(request =>
      request.ownership_rules.some(
        rule => rule.condition_value?.trim() || rule.condition_key_value?.trim()
      )
    );

  const handleRefreshCostPreview = async () => {
    if (!(await validateForm())) {
      toast({ description: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    const currentRequests = buildOwnershipRequests(form.getValues("ownerships"));
    if (!hasValidOwnerships(currentRequests)) {
      toast({ description: "Please fill in condition values", variant: "destructive" });
      return;
    }
    await refreshCostPreview(currentRequests);
  };

  const handleViewConflicts = async () => {
    if (!(await validateForm())) {
      toast({ description: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    const currentRequests = buildOwnershipRequests(form.getValues("ownerships"));
    if (!hasValidOwnerships(currentRequests)) {
      toast({ description: "Please fill in condition values", variant: "destructive" });
      return;
    }
    setOpen(false);
    setConflictingTeamsModalOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <TooltipCore>
          <DialogTrigger asChild>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" aria-label="Add new ownership rule">
                <AddIcon className="al-h-4 al-w-4" />
              </Button>
            </TooltipTrigger>
          </DialogTrigger>
          <TooltipContent>Add New Ownership</TooltipContent>
        </TooltipCore>
        <DialogContent className="!al-max-w-5xl">
          <DialogHeader>
            <DialogTitle>Create New Ownership</DialogTitle>
          </DialogHeader>
          {enableCostAttributionInTeams && (
            <OwnershipCostBanner
              costPreview={costPreview}
              isCostPreviewLoading={isCostPreviewLoading}
              costPreviewError={costPreviewError}
              hasPreviewableOwnerships={hasPreviewableOwnerships}
              onRefresh={handleRefreshCostPreview}
              onViewConflicts={handleViewConflicts}
            />
          )}
          <Form {...form}>
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <form onSubmit={form.handleSubmit(onSubmit)} className="al-space-y-4" role="form">
              <div className="al-max-h-[60vh] al-overflow-y-auto al-px-2">
                <Stack direction="column" gap="xl">
                  {fields.map((field, index) => (
                    <div key={field.id}>
                      <Stack direction="row" align="center" justify="between">
                        <p className="al-text-sm al-font-medium">Ownership {index + 1}</p>
                        {fields.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(index)}
                            className="al-p-1 al-h-8 al-w-8"
                          >
                            <IconTrash size={16} className="al-text-destructive" />
                          </Button>
                        )}
                      </Stack>
                      <OwnershipFormFields form={form} index={index}>
                        <div className="al-mt-0">
                          {[
                            OwnershipConditionKeys.QUERY_TAG,
                            OwnershipConditionKeys.WAREHOUSE_TAG,
                          ].includes(form.watch(`ownerships.${index}.condition_key`)) ? (
                            <OwnershipTagFields form={form} index={index} />
                          ) : (
                            <OwnershipConditionValueField
                              form={form}
                              index={index}
                              userOptions={userOptions}
                            />
                          )}
                        </div>
                      </OwnershipFormFields>
                      <div className="al-my-4">
                        <div className="al-border-t" />
                        <p className="al-text-xs al-text-muted-foreground al-uppercase al-mt-2">
                          AND
                        </p>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addOwnership}
                    className="al-w-full"
                  >
                    <AddIcon className="al-h-4 al-w-4 al-mr-2" />
                    Add Another Ownership Rule
                  </Button>
                </Stack>
              </div>
              <Stack justify="end" className="al-mt-4">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <LoadingButton type="submit" loading={isLoading}>
                  {isLoading ? "Creating..." : "Create Ownership"}
                </LoadingButton>
              </Stack>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      {enableCostAttributionInTeams && (
        <>
          <ConflictingTeamsModal
            open={conflictingTeamsModalOpen}
            onOpenChange={setConflictingTeamsModalOpen}
            ownershipRequests={buildOwnershipRequests(form.getValues("ownerships"))}
            startDate={dateRange.start_date}
            endDate={dateRange.end_date}
            onValidateForm={validateForm}
            onBack={() => {
              setConflictingTeamsModalOpen(false);
              setOpen(true);
            }}
          />
          <ViewConflictsModal
            open={conflictsModalOpen}
            onOpenChange={setConflictsModalOpen}
            ownershipRequests={ownershipRequests}
            startDate={dateRange.start_date}
            endDate={dateRange.end_date}
            onValidateForm={validateForm}
            onBack={() => {
              setConflictsModalOpen(false);
              setOpen(true);
            }}
          />
        </>
      )}
    </>
  );
};

export default AddNewOwnership;
